// Column arm of the open-tab rewrite planner (see `tabRewritePlanner.ts`).
//
// Split out to keep each arm's gate readable: relation references and column
// references answer different questions ("is this token a relation slot?" vs
// "which relation owns this column?") and share only the occurrence type.

import { SQL_KEYWORDS } from '../../sql-grammar/document/editorContextClause';
import type { StatementInfo } from '../../sql-grammar/model';
import { skipContextFor } from '../../sql-resolver/classify/tokenSkip';
import { computeAliasBindingsForRange } from '../../sql-resolver/scope/aliasScope';
import { skipParen } from '../../sql-resolver/scope/aliasScope.scanner';
import { computeCteBindingsForRange } from '../../sql-resolver/scope/cteScope';
import { findReferencedRelationsForRange } from '../../sql-resolver/scope/findReferencedRelations';
import {
  RELATION_INTRODUCERS_WITH_DML,
  relationSlotKind,
} from '../../sql-resolver/scope/relationSlot';
import {
  columnLeavesEqual,
  relationIdentitiesEqual,
  type RelationIdentityContext,
} from '../relationIdentity';
import type { QualifiedName } from '../types';

import {
  forEachIdentifierChain,
  qualifierRelationOf,
  type IdentifierChain,
} from './bufferIdentifierScan';
import { resolveAliasBinding, type BufferContext } from './bufferRenameTarget';
import type { OccurrenceResult } from './tabRewriteOccurrence';

export type ColumnOccurrenceInput = {
  readonly ctx: BufferContext;
  readonly chain: IdentifierChain;
  readonly statement: StatementInfo;
  readonly column: QualifiedName;
  readonly containingTable: QualifiedName;
  readonly identity: RelationIdentityContext;
};

export function matchColumnOccurrence(input: ColumnOccurrenceInput): OccurrenceResult {
  const { ctx, chain, column, containingTable: host, identity } = input;
  // Leaf-name equality through the engine-aware COLUMN comparator, not a raw
  // string compare and not the relation one: on MSSQL `[Email]` (quoted →
  // folding preserves case) and `email` are the same column, on PostgreSQL they
  // are not — and on a MySQL `lower_case_table_names = 0` server a table leaf is
  // case-SIGNIFICANT (it is a file on disk) while a column leaf never is, so
  // routing this through `relationIdentitiesEqual` left a valid `u.ID`
  // unrewritten when `users.id` was renamed. The HOST comparisons below stay on
  // `relationIdentitiesEqual` — they really are about relations, and must keep
  // the mode-0 rule.
  if (!columnLeavesEqual(leafOf(chain), leafOf2(column), identity)) return null;

  const hit = {
    start: chain.terminal.start,
    end: chain.terminal.end,
    quoting: chain.terminalQuoting,
  } as const;

  if (chain.partCount >= 2) {
    // Qualifier resolves to an alias → the alias's target relation is the host.
    // (The alias token itself is never rewritten — only the column tail.)
    //
    // TWO parts only. `resolveAliasBinding` reads the chain's HEAD, and the
    // head is the qualifier in `alias.column` and nowhere else: at three or
    // more parts it is a schema or a database and the relation sits further
    // along. A longer chain that took this shortcut was decided by whatever its
    // head happened to collide with rather than by what it names — with
    // `FROM A.dbo.users B` in scope, the `B.dbo.orders.id` of
    // `SELECT B.dbo.orders.id FROM B.dbo.orders CROSS JOIN A.dbo.users B`
    // resolved `B` to the alias, matched the renamed `A.dbo.users.id`, and
    // rewrote a column of an unrelated table in another database; not one of
    // the schema / database checks below ever ran. Longer chains name their
    // relation explicitly, so `qualifierRelationOf` answers them — including
    // the 3-part `dbo.users.id`, which still rewrites when it really is the
    // host.
    const alias = chain.partCount === 2 ? resolveAliasBinding(ctx, chain) : null;
    if (alias) return relationIdentitiesEqual(alias.target, host, identity) ? hit : 'skip';
    // Otherwise the qualifier names the relation directly (`users.email`,
    // `dbo.users.email` — the trailing two parts are relation + column).
    // `qualifierRelationOf` is the ONE place that reads a qualifier off a
    // chain, spelled and folded halves together: this arm and
    // `tabRewritePlanner.ts`'s three-part relation arm both dropped the spelled
    // schema when each built its own. It answers `null` for a chain whose
    // qualifier cannot be read at all — five parts, where the database is the
    // component the parser discards — and this arm reports that rather than
    // comparing the two thirds of an identity it can see. The HOST it is
    // compared against carries the rename's own database (`inEffectiveDatabase`
    // in `tabRewritePlanner.ts`), so a four-part chain naming a different one
    // is a mismatch instead of a wildcard hit.
    const qualifier = qualifierRelationOf(chain, ctx.options);
    if (!qualifier) return 'skip';
    // An UNQUALIFIED qualifier (`users.email`) names whichever `users` the
    // statement's FROM clause brought into scope — resolve it there before
    // comparing. Comparing the bare `users` against a `dbo.users` host treats
    // the absent schema as a wildcard, which rewrites `SELECT users.email FROM
    // s2.users` for a rename of `dbo.users.email` — a different table.
    const resolved = qualifier.foldedSchema
      ? qualifier
      : (relationInScopeNamed(ctx, input.statement, qualifier, identity) ?? qualifier);
    return relationIdentitiesEqual(resolved, host, identity) ? hit : 'skip';
  }

  // Bare column. Only provable when the statement's FROM scope is a single
  // relation and that relation is the renamed column's table — the same gate
  // the index builder's `emitBareColumnRefs` applies. Anything else is a
  // deliberate, reported skip rather than a silent one.
  if (!chain.head.quoted && SQL_KEYWORDS.has(chain.head.value.toUpperCase())) return null;
  if (inRelationSlot(ctx, chain)) {
    // …except an `INSERT INTO t (col, …)` column-list entry, which follows `(`
    // and so LOOKS like a bare relation slot. It is a column of the insert
    // target, and that target names its host unambiguously — rewrite it when
    // it is the renamed column's table, report it when it is not. Returning
    // null here (the pre-fix behaviour) broke the tab's INSERT in silence.
    const insertHost = insertColumnListRelation(ctx, input.statement, chain.start);
    if (!insertHost) return null;
    return relationIdentitiesEqual(insertHost, host, identity) ? hit : 'skip';
  }
  if (isFunctionCall(ctx.source, chain.end)) return null;
  const only = singleRelationScope(ctx, input.statement);
  if (!only) return 'skip';
  return relationIdentitiesEqual(only, host, identity) ? hit : 'skip';
}

/** Leaf-only identity for the chain's terminal token.
 *
 *  The delimiters read here are the TERMINAL token's own
 *  (`chain.terminalQuoting`), never `chain.qualified.quoted` — that flag is
 *  `parts.some(p => p.quoted)`, i.e. it describes the CHAIN. A quoted QUALIFIER
 *  therefore reported a bare column leaf as quoted, folding preserved its case
 *  (`"NavRnQHost".ROW_ID` stayed `ROW_ID` on PostgreSQL, `"NavRnQHost".row_id`
 *  stayed `row_id` on Oracle), and the leaf then matched neither the renamed
 *  column's folded identity nor anything else: the buffer kept naming a column
 *  the ALTER had renamed away, with `skippedCount` 0 so not even the stale
 *  notice fired.
 *
 *  `qualified.foldedName` is the fold `readQualifiedName` already computed from
 *  that same terminal part's own quoted flag, and it is the fold
 *  `tabRewritePlanner.ts` renders the replacement against — so reading it here
 *  keeps the match and the edit asking one question, rather than re-deriving it
 *  from a flag that answers another. */
function leafOf(chain: IdentifierChain): QualifiedName {
  return {
    name: chain.qualified.name,
    raw: chain.qualified.name,
    quoted: chain.terminalQuoting !== 'unquoted',
    quoting: chain.terminalQuoting,
    foldedName: chain.qualified.foldedName,
  };
}

/** Leaf-only identity for the renamed column (drops any host qualification —
 *  the host is checked separately against `containingTable`). */
function leafOf2(column: QualifiedName): QualifiedName {
  return {
    name: column.name,
    raw: column.name,
    quoted: column.quoted,
    foldedName: column.foldedName ?? column.name,
  };
}

function inRelationSlot(ctx: BufferContext, chain: IdentifierChain): boolean {
  return (
    relationSlotKind(ctx.source, chain.start, chain.end, {
      introducers: RELATION_INTRODUCERS_WITH_DML,
      disambiguateComma: true,
    }) === 'bare'
  );
}

/** The single relation the statement selects from, or null when the scope is
 *  empty / ambiguous (multi-relation join, CTE-backed, derived table). */
function singleRelationScope(
  ctx: BufferContext,
  statement: StatementInfo,
): QualifiedName | null {
  const start = statement.range.startOffset;
  const end = statement.range.endOffset;
  if (computeCteBindingsForRange(ctx.buffer, start, end).length > 0) return null;
  const aliases = computeAliasBindingsForRange(ctx.buffer, start, end);
  if (aliases.length > 1) return null;
  // The alias scanner emits ONLY aliased entries, so an alias count of 1 does
  // NOT prove a single-relation scope: `FROM users u JOIN orders ON …` has one
  // alias and two relations, and a bare `email` there may bind to either.
  // Mirror `columnRefScanner.isSingleRelationScope` (which rejects any join) by
  // additionally requiring the referenced-relation scan to see exactly one.
  const referenced = referencedRelationsSafe(ctx, start, end);
  if (referenced.length !== 1) return null;
  if (aliases.length === 1) {
    const only = aliases[0];
    return only.source !== 'cte' && only.target.name.length > 0 ? only.target : null;
  }
  // Unaliased single relation (`SELECT email FROM users`) — the alias scanner
  // deliberately omits it, so read it off the referenced-relation scan.
  return referenced[0];
}

/** The insert target of an `INSERT INTO <relation> ( … )` whose column list
 *  contains `offset`, or null when the offset is not inside such a list. */
function insertColumnListRelation(
  ctx: BufferContext,
  statement: StatementInfo,
  offset: number,
): QualifiedName | null {
  const end = statement.range.endOffset;
  const skipCtx = skipContextFor(ctx.options);
  let afterInto = false;
  let found: QualifiedName | null = null;
  forEachIdentifierChain(
    ctx.source,
    ctx.options,
    statement.range.startOffset,
    end,
    (candidate) => {
      if (candidate.start > offset) return false;
      if (!afterInto) {
        afterInto =
          candidate.partCount === 1 &&
          !candidate.head.quoted &&
          candidate.head.value.toUpperCase() === 'INTO';
        return true;
      }
      afterInto = false;
      // `candidate` is the insert target. Its column list, when present, is the
      // paren group immediately after it.
      const open = nextSignificantIndex(ctx.source, candidate.end, end);
      if (open < 0 || ctx.source[open] !== '(') return true;
      const close = skipParen(ctx.source, open, end, skipCtx) - 1;
      if (offset > open && offset < close) {
        found = candidate.qualified;
        return false;
      }
      return true;
    },
  );
  return found;
}

/** Index of the next non-whitespace, non-comment byte at or after `from`, or
 *  -1 when the range holds none. */
function nextSignificantIndex(source: string, from: number, end: number): number {
  let i = from;
  while (i < end) {
    if (/\s/.test(source[i])) { i += 1; continue; }
    if (source[i] === '-' && source[i + 1] === '-') {
      const nl = source.indexOf('\n', i);
      i = nl < 0 ? end : nl + 1;
      continue;
    }
    if (source[i] === '/' && source[i + 1] === '*') {
      const close = source.indexOf('*/', i + 2);
      i = close < 0 ? end : close + 2;
      continue;
    }
    return i;
  }
  return -1;
}

/** The statement's FROM-clause relation whose leaf name is `qualifier`, with
 *  its schema/database as SPELLED there. Null when the statement names no such
 *  relation (the caller then falls back to the bare qualifier). */
function relationInScopeNamed(
  ctx: BufferContext,
  statement: StatementInfo,
  qualifier: QualifiedName,
  identity: RelationIdentityContext,
): QualifiedName | null {
  const referenced = referencedRelationsSafe(
    ctx,
    statement.range.startOffset,
    statement.range.endOffset,
  );
  return referenced.find((r) => relationIdentitiesEqual(r, qualifier, identity)) ?? null;
}

function referencedRelationsSafe(
  ctx: BufferContext,
  start: number,
  end: number,
): readonly QualifiedName[] {
  try {
    return findReferencedRelationsForRange(ctx.buffer, start, end);
  } catch {
    return [];
  }
}

function isFunctionCall(source: string, end: number): boolean {
  let j = end;
  while (j < source.length && /\s/.test(source[j])) j += 1;
  return source[j] === '(';
}
